import { useEffect, useRef } from 'react';
import { useIncidentStore } from '@/store/incident.store';
import { useTenantQuery } from './useTenantQuery';
import { getErrorStatus } from '@/config/queryClient';
import { Incident } from '@/types/incident';

/**
 * Rehydrates the active incident from the server when only the ID
 * was persisted to localStorage (e.g. after page reload).
 *
 * Mount once near the app root. When activeIncidentId is set but
 * activeIncident is null, fetches fresh data from GET /incidents/:id
 * and populates the store.
 *
 * Failure semantics:
 * - 404 / 403 -> incident is genuinely gone or forbidden. Clear context so
 *   the user is prompted to pick a new incident.
 * - 429 / 5xx / network -> transient. Keep the persisted activeIncidentId
 *   so that the sidebar and downstream RequireIncident guards continue to
 *   treat the incident as engaged; React Query will retry per the global
 *   policy and rehydrate once the call succeeds.
 */
export function useActiveIncidentSync() {
  const activeIncidentId = useIncidentStore((s) => s.activeIncidentId);
  const activeIncident = useIncidentStore((s) => s.activeIncident);
  const isDailyOps = useIncidentStore((s) => s.isDailyOps);
  const setActiveIncident = useIncidentStore((s) => s.setActiveIncident);
  const clearIncidentContext = useIncidentStore((s) => s.clearIncidentContext);
  const adoptServerContext = useIncidentStore((s) => s.adoptServerContext);

  // ---------------------------------------------------------------------
  // Reconcile with the server's user_contexts row.
  //
  // Engaging an incident PUTs /user/context, but nothing ever issued a GET, so
  // localStorage was the sole source of truth for which incident is engaged. Two
  // consequences, both observed in production: engaging on one device left every other
  // device pinned to the previous incident forever, and a context cleared server-side
  // survived a full page reload on the client — which then kept rendering
  // incident-scoped pages and prefilling new records from the stale incident.
  //
  // The server row is the durable record (it is what this client writes on every
  // engage), so it wins when the two disagree. Reconciliation only runs on a
  // SUCCESSFUL fetch, so an offline engage is never clobbered by a failed request.
  // ---------------------------------------------------------------------
  const { data: serverContext } = useTenantQuery<{
    active_incident_id: string | null;
    is_daily_ops: boolean;
  }>(['user', 'context'], '/user/context', { staleTime: 0 });

  // Reconcile ONCE, on the first successful read. Re-running on every refetch would let a
  // late-arriving response overwrite an incident the user engaged in the meantime — the
  // server row is the authority for what was engaged BEFORE this session started, not a
  // continuous master that should fight the user mid-session. Subsequent engages write to
  // the server themselves, so the two stay in step without this effect re-firing.
  const reconciledRef = useRef(false);
  useEffect(() => {
    if (!serverContext || reconciledRef.current) return;
    reconciledRef.current = true;
    adoptServerContext(serverContext.active_incident_id ?? null);
  }, [serverContext, adoptServerContext]);

  const needsFetch = !!activeIncidentId && !activeIncident && !isDailyOps;

  const { data, error } = useTenantQuery<Incident>(
    ['incidents', activeIncidentId, 'rehydrate'],
    `/incidents/${activeIncidentId}`,
    { enabled: needsFetch, staleTime: 0 }
  );

  useEffect(() => {
    if (data && needsFetch) {
      setActiveIncident(data);
    }
  }, [data, needsFetch, setActiveIncident]);

  useEffect(() => {
    if (!error || !needsFetch) return;
    const status = getErrorStatus(error);
    // Only clear on definitive "this incident is gone or you can't see it"
    // responses. Treat 429/5xx/network failures as transient — the React
    // Query retry policy will eventually succeed and the store will be
    // repopulated. Clearing on transient errors silently breaks every
    // incident-scoped page until the user re-engages.
    if (status === 404 || status === 403) {
      clearIncidentContext();
    }
  }, [error, needsFetch, clearIncidentContext]);

  return { isRehydrating: needsFetch };
}
